import { Navigate } from "react-router-dom";
import paths from "routing/paths";
import { useEffect, useState } from "react";
import { supportsStorage } from "utils";
import Loader from "components/Loader/Loader";
import { BASE_URL } from "utils/environmentVariables";
import {useAddLogInToHistoryMutation} from "../../../services";
import {getCurrentUserDetails} from "../../../services/auth/auth.api";
import {ParsedUserJWT} from "../../../interfaces/Cognito";

const SSO_LOGIN_LOGGED_KEY = 'ssoLoginLogged';

const AuthRedirect = () => {
  const [to, _to] = useState<
    { url: string; external: boolean } | null | undefined
  >(undefined);
  
  // New state to ensure authentication processing is complete before navigating
  const [isAuthProcessingComplete, setIsAuthProcessingComplete] = useState(false);
  
  // Initialize your Redux Toolkit Query mutation hook
  const [addLogInToHistory] = useAddLogInToHistoryMutation();

  useEffect(() => {
    if (supportsStorage("localStorage")) {
      const local = localStorage.getItem("location") as string,
        persistedLocation = !!local ? JSON.parse(local) : null;
      const to = !!persistedLocation
        ? persistedLocation.search
          ? persistedLocation.pathname + persistedLocation.search
          : persistedLocation.hash
          ? persistedLocation.pathname + persistedLocation.hash
          : persistedLocation.pathname
        : null;

      _to({ url: to, external: persistedLocation?.external || false });
    } else {
      _to(null);
    }
  }, []);
  
  // Process authentication, detect SSO, and log the event
  useEffect(() => {
    const processAuthAndLog = async () => {
      try {
        // This is the crucial step: getCurrentUserDetails() will implicitly
        // trigger Amplify to parse the URL parameters (e.g., #id_token=... or ?code=...)
        // and establish the user's session.
        const userDetailsResult = await getCurrentUserDetails();
        const currentUserDetails: ParsedUserJWT | null | undefined = userDetailsResult.currentUserDetails;
        
        if (currentUserDetails) {
          // User is authenticated. Now, check if it's an SSO user and if we need to log.
          if (currentUserDetails.isSSO && !sessionStorage.getItem(SSO_LOGIN_LOGGED_KEY)) {
            // Call your existing Redux Toolkit Query mutation
            // Since it doesn't take parameters, just call it directly.
            await addLogInToHistory().unwrap()
              .then(() => {
                sessionStorage.setItem(SSO_LOGIN_LOGGED_KEY, 'true'); // Mark as logged for this session
              })
              .catch((e:any) => {
                console.error('AuthRedirect: Failed to log SSO event via mutation:', e);
              })
              .finally(() => {
                setIsAuthProcessingComplete(true); // Mark authentication processing as complete
              }); // .unwrap() to catch errors
          }
        } else {
          // If no user details, it means authentication failed or user is not logged in.
          console.log('AuthRedirect: No authenticated user found after redirect.');
          // You might want to explicitly redirect to the login page here if this is an unexpected state
          // e.g., if the user came from an SSO link but authentication failed.
          // navigate(paths.LOGIN); // Requires useNavigate hook
        }
      } catch (error) {
        console.error('AuthRedirect: Error during authentication processing:', error);
        // This could happen if Amplify fails to parse tokens or network issues.
        // Consider redirecting to an error page or login page.
      }
    };
    
    processAuthAndLog();
    
  }, []);

  useEffect(() => {
    if (to !== undefined && supportsStorage("localStorage")) {
      localStorage.removeItem("location");
    }

    if (to?.external) {
      window.location.href = BASE_URL + to.url;
    }
  }, [to]);

  return to === undefined || !isAuthProcessingComplete || to?.external ? (
    <Loader active />
  ) : !!to?.url ? (
    <Navigate to={to.url} replace />
  ) : (
    <Navigate to={paths.HOME} replace />
  );
};

export default AuthRedirect;
